// LearningPage — the Learning / Procedures library. Portal users get a clean,
// read-only set of how-to guides; PR staff (editors) can create/publish and see
// read counts. Articles are grouped by category.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, CheckCircle2, Circle, Eye, FileText as FileIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { usePageHeaderEffect } from "@/context/PageHeaderContext";
import { articleIcon } from "./iconMap";
import ArticleEditorDialog from "@/components/learning/ArticleEditorDialog";

const BRAND = "var(--brand-accent, #C75A1B)";
const EDITOR_ROLES = new Set(["director", "administrator", "consultant"]);

const AUDIENCE_LABEL = { client: "Client portal", pr: "Priority Risk", all: "Everyone" };

export default function LearningPage() {
    const { user } = useAuth();
    const { isInternalView } = useView();
    const navigate = useNavigate();
    const isEditor = user && EDITOR_ROLES.has(user.role);
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | {} (new) | article
    const [audFilter, setAudFilter] = useState("all"); // internal-only audience filter

    usePageHeaderEffect({ title: "Help & Guides", subtitle: "Step-by-step procedures and how-to articles" }, []);

    const load = () => {
        setLoading(true);
        api.get("/learning/articles")
            .then((r) => setArticles(r.data || []))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load guides"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const grouped = useMemo(() => {
        const g = {};
        for (const a of articles) {
            if (isEditor && audFilter !== "all" && a.audience !== audFilter) continue;
            (g[a.category] = g[a.category] || []).push(a);
        }
        return Object.entries(g);
    }, [articles, isEditor, audFilter]);

    const unreadCount = articles.filter((a) => !a.read).length;

    if (loading) {
        return <div className="text-center py-16 text-[#9A968F]"><Loader2 size={18} className="inline animate-spin mr-2" />Loading guides…</div>;
    }

    return (
        <div data-testid="learning-page" className="space-y-7 max-w-5xl">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    {isInternalView && (
                        <>
                            <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                                style={{ borderBottom: `2px solid ${BRAND}`, color: "#6F6A64" }}>Learning</span>
                            <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Help &amp; Guides</h2>
                        </>
                    )}
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {articles.length} guide{articles.length === 1 ? "" : "s"}
                        {unreadCount > 0 ? ` · ${unreadCount} unread` : " · all read"}
                    </p>
                </div>
                {isEditor && (
                    <button data-testid="learning-new-article" onClick={() => setEditing({})}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[9px] text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
                        style={{ background: BRAND }}>
                        <Plus size={15} /> New guide
                    </button>
                )}
            </div>

            {isEditor && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="learning-audience-filter">
                    <span className="text-[11px] font-semibold uppercase tracking-wider mr-1" style={{ color: "#9A968F" }}>Show</span>
                    {[
                        { v: "all", label: "All guides" },
                        { v: "client", label: "Client portal" },
                        { v: "pr", label: "Priority Risk" },
                    ].map((o) => (
                        <button key={o.v} data-testid={`learning-filter-${o.v}`} onClick={() => setAudFilter(o.v)}
                            className={`px-2.5 py-1 rounded-full text-[11.5px] font-semibold border transition-colors ${audFilter === o.v ? "text-white" : "text-[#6F6A64] hover:bg-[#F6F4F0]"}`}
                            style={audFilter === o.v ? { background: BRAND, borderColor: BRAND } : { borderColor: "#E5E0DA" }}>
                            {o.label}
                        </button>
                    ))}
                </div>
            )}

            {articles.length === 0 ? (
                <div className="bg-white border rounded-[14px] p-10 text-center" style={{ borderColor: "#E5E0DA" }}>
                    <FileIcon size={26} className="mx-auto mb-3" style={{ color: "#C9C4BC" }} />
                    <p className="text-[13.5px] text-[#6F6A64]">No guides published yet.</p>
                    {isEditor && <p className="text-[12px] mt-1 text-[#9A968F]">Click <b>New guide</b> to write the first one.</p>}
                </div>
            ) : (
                grouped.map(([category, items]) => (
                    <section key={category}>
                        <h3 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2.5">{category}</h3>
                        <div className="grid gap-3 sm:grid-cols-2">
                            {items.map((a) => {
                                const Icon = articleIcon(a.icon);
                                return (
                                    <button key={a.id} data-testid={`learning-card-${a.slug}`}
                                        onClick={() => navigate(`/app/learning/${a.id}`)}
                                        className="group text-left bg-white border rounded-[14px] p-4 flex gap-3.5 transition-all hover:shadow-brand hover:-translate-y-0.5"
                                        style={{ borderColor: "#E5E0DA" }}>
                                        <div className="w-11 h-11 rounded-[11px] grid place-items-center flex-shrink-0"
                                            style={{ background: "#FBEFE5", color: BRAND }}>
                                            <Icon size={20} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <h4 className="text-[14px] font-bold text-[#1F1F1F] leading-tight truncate flex-1">{a.title}</h4>
                                                {a.read
                                                    ? <CheckCircle2 size={15} className="flex-shrink-0" style={{ color: "#3F7D3A" }} />
                                                    : <Circle size={9} className="flex-shrink-0" style={{ color: BRAND, fill: BRAND }} />}
                                            </div>
                                            {a.summary && <p className="text-[12px] mt-1 leading-snug line-clamp-2" style={{ color: "#6F6A64" }}>{a.summary}</p>}
                                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                                                {a.status === "draft" && (
                                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#FCF1DC", color: "#C68A1C" }}>Draft</span>
                                                )}
                                                {isEditor && (
                                                    <>
                                                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#EEE9F6", color: "#6B4E9E" }}>
                                                            {AUDIENCE_LABEL[a.audience] || a.audience}
                                                        </span>
                                                        <span className="inline-flex items-center gap-1 text-[10.5px]" style={{ color: "#9A968F" }}>
                                                            <Eye size={11} /> {a.read_count} read
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                ))
            )}

            {editing !== null && (
                <ArticleEditorDialog
                    article={editing.id ? editing : null}
                    onClose={() => setEditing(null)}
                    onSaved={(a) => { setEditing(null); if (a?.id) navigate(`/app/learning/${a.id}`); else load(); }}
                />
            )}
        </div>
    );
}
